import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { formatDuration } from '@/utils/formatters';
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';

export default function AudioPlayer() {
  const {
    currentSong,
    currentRevision,
    isPlaying,
    currentTime,
    duration,
    queue,
    currentIndex,
    togglePlayPause,
    playNext,
    playPrevious,
    seek,
  } = useAudioPlayer();

  if (!currentSong) return null;

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    seek(Number(e.target.value));
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border p-4 z-50">
      <div className="max-w-7xl mx-auto">
        {/* Song Info and Controls */}
        <div className="flex items-center gap-4 mb-2">
          {/* Song Info */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">
              {currentRevision ? currentRevision.songName : currentSong.songName}
            </p>
            <p className="text-text-secondary text-sm truncate">
              {currentRevision ? `${currentSong.songName}` : ''}
            </p>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center gap-2">
            {/* Previous Button */}
            {queue.length > 0 && currentIndex > 0 && (
              <button
                onClick={playPrevious}
                className="text-text-secondary hover:text-white transition-colors"
                aria-label="Previous track"
              >
                <SkipBack size={30} />
              </button>
            )}

            {/* Play/Pause Button */}
            <button
              onClick={togglePlayPause}
              className="bg-primary text-black rounded-full p-3 hover:bg-primary-hover transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
            </button>

            {/* Next Button */}
            {queue.length > 0 && currentIndex < queue.length - 1 && (
              <button
                onClick={playNext}
                className="text-text-secondary hover:text-white transition-colors"
                aria-label="Next track"
              >
                <SkipForward size={30} />
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
            style={{
              background: `linear-gradient(to right, #00d4ff ${progress}%, #3a3a3a ${progress}%)`,
            }}
            aria-label="Seek"
          />
          {/* Elapsed / total time (Spotify-style: current left, total right) */}
          <div className="flex justify-between mt-1 text-xs text-text-muted tabular-nums">
            <span>{formatDuration(currentTime)}</span>
            <span>{formatDuration(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
